
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { CommunicationTemplate } from '@/services/communication/templates';
import { LeadStatus } from '@/types/lead';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const templateSchema = z.object({
  name: z.string().min(3, 'Template name must be at least 3 characters'),
  content: z.string().min(10, 'Template content must be at least 10 characters'),
  leadStatus: z.string().optional(),
  category: z.string().optional(),
  isDefault: z.boolean().default(false),
});

type TemplateFormValues = z.infer<typeof templateSchema>;

interface TemplateFormProps {
  template?: Partial<CommunicationTemplate>;
  type: 'email' | 'call';
  onSave: (template: Partial<CommunicationTemplate>) => Promise<void>;
  onCancel: () => void;
}

const TemplateForm: React.FC<TemplateFormProps> = ({ 
  template, 
  type, 
  onSave, 
  onCancel 
}) => {
  const [isSaving, setIsSaving] = useState(false);
  
  const form = useForm<TemplateFormValues>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: template?.name || '',
      content: template?.content || '',
      leadStatus: template?.leadStatus || undefined,
      category: template?.category || undefined,
      isDefault: template?.isDefault || false,
    },
  });

  const handleSubmit = async (values: TemplateFormValues) => {
    setIsSaving(true);
    try {
      await onSave({
        ...template,
        ...values,
        type,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const leadStatusOptions: { value: LeadStatus; label: string }[] = [
    { value: 'new', label: 'New Lead' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'proposal', label: 'Proposal' },
    { value: 'negotiation', label: 'Negotiation' },
    { value: 'closed_won', label: 'Closed (Won)' },
    { value: 'closed_lost', label: 'Closed (Lost)' },
  ];

  const categoryOptions = [
    { value: 'introduction', label: 'Introduction' },
    { value: 'follow_up', label: 'Follow-up' },
    { value: 'proposal', label: 'Proposal' },
    { value: 'meeting_request', label: 'Meeting Request' },
    { value: 'thank_you', label: 'Thank You' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{template?.id ? 'Edit' : 'Create'} {type === 'email' ? 'Email Template' : 'Call Script'}</CardTitle>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Template Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter a name for this template" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="leadStatus"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lead Status</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a lead status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-background border border-border shadow-lg z-[100]">
                        {leadStatusOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Associate this template with a specific lead status
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-background border border-border shadow-lg z-[100]">
                        {categoryOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Categorize this template for easier organization
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Content</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder={`Write your ${type === 'email' ? 'email content' : 'call script'} here...`}
                      className="min-h-[200px]"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Template'}
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
};

export default TemplateForm;
